import {
  FaBell,
  FaChalkboardTeacher,
  FaChartLine,
  FaClipboardCheck,
  FaClipboardList,
  FaCloud,
  FaMobileAlt,
  FaMoneyCheckAlt,
  FaTools,
  FaUserGraduate,
} from "react-icons/fa";

export const services = [
  {
    id: 1,
    slug: "academic-management",
    title: "Academic Management",
    desc: "Create courses, manage syllabus, timetables and teacher allocations with smart planners.",
    icon: FaChalkboardTeacher,
    color: "from-orange-100 to-orange-50",
    accent: "from-orange-500 to-amber-500",
    eyebrow: "Curriculum control",
    overview:
      "Coordinate classes, subjects, timetables, faculty allocation and syllabus planning from one academic workspace.",
    spotlight:
      "Built for schools that want academic operations to move with less manual coordination and fewer timetable conflicts.",
    capabilities: [
      "Plan subjects, class structures and faculty allocation in one place.",
      "Publish timetables and academic calendars with role-based visibility.",
      "Track syllabus completion and upcoming academic milestones.",
      "Reduce operational drift across branches, grades and coordinators.",
    ],
    outcomes: [
      "Faster timetable planning",
      "Better teacher workload visibility",
      "Clearer academic execution",
    ],
    idealFor: "Principals, vice principals, academic coordinators and department heads.",
  },
  {
    id: 2,
    slug: "admissions-enquiries",
    title: "Admissions & Enquiries",
    desc: "Online admission forms, payment integration, application tracking and automated confirmations.",
    icon: FaUserGraduate,
    color: "from-yellow-100 to-yellow-50",
    accent: "from-yellow-500 to-orange-500",
    eyebrow: "Enrollment pipeline",
    overview:
      "Turn admission operations into a structured digital flow from enquiry capture to application review and confirmation.",
    spotlight:
      "Designed for institutions that want fewer spreadsheets and a smoother parent experience during intake seasons.",
    capabilities: [
      "Capture online applications with structured student and parent data.",
      "Track enquiry stages, pending documents and admission decisions.",
      "Connect payment collection to registration and application workflows.",
      "Send timely confirmations, reminders and next-step updates automatically.",
    ],
    outcomes: [
      "Higher response speed",
      "Cleaner admission tracking",
      "Less front-desk follow-up",
    ],
    idealFor: "Admission officers, school admins, front office teams and principals.",
  },
  {
    id: 3,
    slug: "attendance-roll-calls",
    title: "Attendance & Roll Calls",
    desc: "Quick attendance by class, biometric/QR integrations and attendance analytics.",
    icon: FaClipboardList,
    color: "from-indigo-100 to-indigo-50",
    accent: "from-indigo-500 to-blue-500",
    eyebrow: "Daily presence tracking",
    overview:
      "Record daily attendance faster and turn class-level check-ins into actionable visibility for staff and parents.",
    spotlight:
      "Useful for schools that need dependable day-to-day attendance records without slowing down teachers in the classroom.",
    capabilities: [
      "Mark attendance by class, section or subject period.",
      "Support integration paths for biometric or QR-enabled attendance flows.",
      "Identify irregular attendance patterns early through analytics.",
      "Notify families or administrators when thresholds are crossed.",
    ],
    outcomes: [
      "Faster class attendance",
      "Earlier intervention",
      "Clearer absentee reporting",
    ],
    idealFor: "Teachers, coordinators, discipline teams and parent communication teams.",
  },
  {
    id: 4,
    slug: "fee-management-payments",
    title: "Fee Management & Payments",
    desc: "Online payments, automated invoices, fee receipts, instalment schedules and reminders.",
    icon: FaMoneyCheckAlt,
    color: "from-green-100 to-green-50",
    accent: "from-emerald-500 to-green-500",
    eyebrow: "Revenue operations",
    overview:
      "Manage invoices, fee schedules, receipts and reminders through a single fee collection and reconciliation workflow.",
    spotlight:
      "A strong fit for schools looking to reduce delayed payments and cut manual fee ledger work.",
    capabilities: [
      "Configure recurring fees, instalments, concessions and due dates.",
      "Generate receipts and payment records with structured student mapping.",
      "Automate reminders before and after due dates.",
      "Surface collection summaries for finance teams and administrators.",
    ],
    outcomes: [
      "Cleaner fee records",
      "Fewer missed follow-ups",
      "Better payment visibility",
    ],
    idealFor: "Accounts teams, cashiers, school admins and management teams.",
  },
  {
    id: 5,
    slug: "exams-assessments",
    title: "Exams & Assessments",
    desc: "Create exams, question banks, automated grading, result generation and progress reports.",
    icon: FaClipboardCheck,
    color: "from-pink-100 to-pink-50",
    accent: "from-pink-500 to-rose-500",
    eyebrow: "Assessment workflows",
    overview:
      "Run examination cycles with more structure, from exam setup and grading to report generation and performance review.",
    spotlight:
      "Useful for institutions that want assessment processing to be more consistent and less dependent on manual compilation.",
    capabilities: [
      "Configure exam terms, subjects, weightages and grading schemes.",
      "Maintain question banks and streamline evaluation operations.",
      "Generate student results and report cards faster.",
      "Analyze subject and class performance trends over time.",
    ],
    outcomes: [
      "Shorter result cycles",
      "More consistent grading",
      "Improved academic insight",
    ],
    idealFor: "Exam coordinators, teachers, academic heads and administrators.",
  },
  {
    id: 6,
    slug: "learning-management-lms",
    title: "Learning Management (LMS)",
    desc: "Host lessons, assignments, video content and track student progress inside the portal.",
    icon: FaMobileAlt,
    color: "from-blue-100 to-blue-50",
    accent: "from-sky-500 to-blue-500",
    eyebrow: "Digital classroom",
    overview:
      "Deliver lessons, assignments and learning material through a structured digital learning environment connected to school operations.",
    spotlight:
      "Helps schools centralize content delivery instead of scattering learning resources across tools and chats.",
    capabilities: [
      "Upload lessons, assignments, media and supplementary material.",
      "Track assignment completion and learner engagement patterns.",
      "Support blended learning across classroom and remote scenarios.",
      "Keep content access organized by class, section and role.",
    ],
    outcomes: [
      "Stronger content delivery",
      "Better student follow-through",
      "Cleaner digital learning structure",
    ],
    idealFor: "Teachers, academic teams, coordinators and digital learning leads.",
  },
  {
    id: 7,
    slug: "notifications-communication",
    title: "Notifications & Communication",
    desc: "SMS, email & in-app push notifications — announcements, homework and parent communication.",
    icon: FaBell,
    color: "from-rose-100 to-rose-50",
    accent: "from-rose-500 to-orange-500",
    eyebrow: "School communication",
    overview:
      "Deliver announcements, reminders and updates with better targeting across parents, staff and students.",
    spotlight:
      "Ideal for schools that need communication to be fast, traceable and less dependent on fragmented channels.",
    capabilities: [
      "Send multi-channel notices using SMS, email or in-app alerts.",
      "Target messages by class, role, branch or audience segment.",
      "Keep school-wide announcements and urgent notices centralized.",
      "Support academic, operational and emergency communication flows.",
    ],
    outcomes: [
      "Fewer missed updates",
      "Faster parent reach",
      "More organized notice delivery",
    ],
    idealFor: "Admins, principals, class coordinators and communication desks.",
  },
  {
    id: 8,
    slug: "analytics-reports",
    title: "Analytics & Reports",
    desc: "Custom dashboards, attendance trends, fee reports and performance analytics for admins.",
    icon: FaChartLine,
    color: "from-cyan-100 to-cyan-50",
    accent: "from-cyan-500 to-sky-500",
    eyebrow: "Decision visibility",
    overview:
      "Give leadership teams a clearer view of school performance through dashboards, trends and actionable reporting.",
    spotlight:
      "Best suited for operators who need visibility across admissions, finance, academics and daily execution.",
    capabilities: [
      "Monitor attendance, collection and academic trends through dashboards.",
      "Break down performance by class, period, module or role.",
      "Support faster review meetings with ready-to-read reports.",
      "Reduce dependence on manually assembled status summaries.",
    ],
    outcomes: [
      "Faster decisions",
      "More visible trends",
      "Sharper operational reviews",
    ],
    idealFor: "Management teams, principals, finance heads and operations leaders.",
  },
  {
    id: 9,
    slug: "transport-logistics",
    title: "Transport & Logistics",
    desc: "Manage routes, vehicles, driver records and track pick-ups with live updates.",
    icon: FaTools,
    color: "from-lime-100 to-lime-50",
    accent: "from-lime-500 to-emerald-500",
    eyebrow: "Mobility coordination",
    overview:
      "Coordinate school transport routes, records and operational visibility through a single transport management layer.",
    spotlight:
      "Useful for institutions handling route planning, transport safety and parent-facing pickup coordination.",
    capabilities: [
      "Organize vehicle, route and driver information centrally.",
      "Track route assignments and transport coverage by student group.",
      "Support live update patterns for pickup and drop communication.",
      "Create clearer transport accountability and record maintenance.",
    ],
    outcomes: [
      "Better route clarity",
      "Safer transport records",
      "Less manual coordination",
    ],
    idealFor: "Transport managers, operations admins and school support teams.",
  },
  {
    id: 10,
    slug: "integrations-cloud",
    title: "Integrations & Cloud",
    desc: "Seamless integrations (payment gateways, identity providers) with secure cloud hosting.",
    icon: FaCloud,
    color: "from-slate-100 to-slate-50",
    accent: "from-slate-700 to-slate-900",
    eyebrow: "Platform foundation",
    overview:
      "Extend the platform with secure integrations and scalable cloud infrastructure aligned with modern school operations.",
    spotlight:
      "Designed for schools that want ClassNX to fit existing payment, identity or digital service ecosystems.",
    capabilities: [
      "Connect external services such as payment or identity providers.",
      "Support secure cloud deployment and centralized access patterns.",
      "Reduce operational silos through connected platform workflows.",
      "Prepare the system for growth across campuses or future modules.",
    ],
    outcomes: [
      "Stronger platform flexibility",
      "Better system continuity",
      "More scalable operations",
    ],
    idealFor: "Technology leads, management teams and multi-campus operators.",
  },
];

export const getServiceBySlug = (slug) =>
  services.find((service) => service.slug === slug);